import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RuleIcon from '@mui/icons-material/Rule';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';

type DocsView = 'cards' | 'how-to-create' | 'rule-instructions' | 'adobe-analytics' | 'bulk-upload';

interface DocsPageProps {
  onNavigate?: (view: string) => void;
  initialSection?: string;
}

const DocsPage: React.FC<DocsPageProps> = ({ onNavigate, initialSection }) => {
  const [docsView, setDocsView] = useState<DocsView>(
    (initialSection as DocsView) || 'cards'
  );

  const cards = [
    {
      id: 'how-to-create' as DocsView,
      icon: <MenuBookIcon sx={{ fontSize: 40, color: '#2196F3' }} />,
      title: 'How to Create a Rule',
      description: 'Step-by-step guide to create a QR redirect rule from start to submission.',
    },
    {
      id: 'rule-instructions' as DocsView,
      icon: <ListAltIcon sx={{ fontSize: 40, color: '#4CAF50' }} />,
      title: 'Rule Instructions & Features',
      description: 'Detailed instructions on rule fields, user roles, scheduling, statuses, and matching criteria.',
    },
    {
      id: 'adobe-analytics' as DocsView,
      icon: <BarChartIcon sx={{ fontSize: 40, color: '#FF5722' }} />,
      title: 'How to Request Access',
      description: 'How to request access for Claravine and Adobe Analytics for QR redirect tracking.',
    },
    {
      id: 'bulk-upload' as DocsView,
      icon: <CloudUploadIcon sx={{ fontSize: 40, color: '#AB47BC' }} />,
      title: 'Bulk Rule Creation',
      description: 'Import multiple QR redirect rules at once using an Excel file for faster rule management.',
    },
  ];

  if (docsView === 'how-to-create') {
    return (
      <Box sx={{ flex: 1, p: 4, background: '#fff', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
              How to Create a Rule
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: '#1a1a1a', mb: 3, fontSize: '17px' }}>
            Create smart redirect rules that dynamically route QR code scans to the right destination based on location, product, or time criteria.
          </Typography>

          <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: '20px', color: '#1a1a1a' }}>
              How It Works
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center', p: 1.5, flex: '1 1 120px', minWidth: 120 }}>
                <QrCode2Icon sx={{ fontSize: 36, color: '#2196F3' }} />
                <Typography variant="body1" sx={{ fontWeight: 700, mt: 1, fontSize: '16px' }}>Scan QR Code</Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '15px' }}>Consumer scans a Nike QR code</Typography>
              </Box>
              <ArrowRightAltIcon sx={{ color: '#666', fontSize: 48, flexShrink: 0 }} />
              <Box sx={{ textAlign: 'center', p: 1.5, flex: '1 1 120px', minWidth: 120 }}>
                <RuleIcon sx={{ fontSize: 36, color: '#4CAF50' }} />
                <Typography variant="body1" sx={{ fontWeight: 700, mt: 1, fontSize: '16px' }}>Rules Applied</Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '15px' }}>System matches by criteria</Typography>
              </Box>
              <ArrowRightAltIcon sx={{ color: '#666', fontSize: 48, flexShrink: 0 }} />
              <Box sx={{ textAlign: 'center', p: 1.5, flex: '1 1 120px', minWidth: 120 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 36, color: '#FF9800' }} />
                <Typography variant="body1" sx={{ fontWeight: 700, mt: 1, fontSize: '16px' }}>Destination</Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '15px' }}>Redirected to targeted experience</Typography>
              </Box>
            </Box>
          </Paper>

          {[
            { step: 1, text: 'Log in to the QR Redirect Automation dashboard' },
            { step: 2, text: 'Click on "Rules" in the sidebar' },
            { step: 3, text: 'On the Rules page, click the blue circle button at the bottom-right corner of the page to create a new rule' },
            { step: 4, text: 'Fill in the General Information section:', bullets: [
              'Enter a Rule Name (must be unique)',
              'Enter the Redirect URL (the destination link where users will land after scanning)',
              'Select the Priority for the rule',
            ]},
            { step: 5, text: 'Fill in the Time & Schedule section:', bullets: [
              'Set the Start Date (when the rule becomes active)',
              'Set the End Date (when the rule stops)',
              'If the rule has no end date, select the "Rule has no expiration date" option',
            ]},
            { step: 6, text: 'Fill in the Matching Criteria section:', link: true, bullets: [
              'Select the fields based on your rule requirements (e.g., Location, Product, Category, Brand, etc.)',
              'Fill in the selected fields accordingly',
            ]},
            { step: 7, text: 'Review all the details you entered' },
            { step: 8, text: 'Click "Submit" — the rule will go to the Approvals page' },
            { step: 9, text: 'An admin will review and approve or reject the rule' },
            { step: 10, text: 'Once approved, the rule goes live automatically based on the start date of the rule' },
          ].map((item) => (
            <Box key={item.step} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#000', minWidth: 24, mt: 0.1 }}>
                  {item.step}.
                </Typography>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '17px' }}>
                    {item.text}
                    {item.link && (
                      <Typography
                        component="span"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); setDocsView('rule-instructions'); }}
                        sx={{ fontSize: '17px', color: '#1565C0', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: '#0D47A1' } }}
                      >
                        {' '}(see Rule Creation Instructions & Features for details)
                      </Typography>
                    )}
                  </Typography>
                  {item.bullets && (
                    <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                      {item.bullets.map((b) => (
                        <Typography key={b} component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.3 }}>
                          {b}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}

          <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#e3f2fd', borderRadius: 2, mt: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#1565C0', mb: 1, fontSize: '17px' }}>Note</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <CloudUploadIcon sx={{ fontSize: 20, color: '#1565C0', mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px' }}>Use <strong>Bulk Upload</strong> to import multiple rules at once via an Excel file.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <LinkIcon sx={{ fontSize: 20, color: '#1565C0', mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px' }}>Double-check the <strong>Redirect URL</strong> before submitting — once a rule is live, users will be redirected immediately.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <DriveFileRenameOutlineIcon sx={{ fontSize: 20, color: '#1565C0', mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px' }}>Each <strong>Rule Name</strong> must be unique — duplicate rule names are not allowed.</Typography>
            </Box>
          </Paper>
      </Box>
    );
  }

  if (docsView === 'rule-instructions') {
    const sectionTitle = (text: string) => (
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '20px', color: '#1a1a1a', mt: 3, mb: 1.5 }}>
        {text}
      </Typography>
    );

    const fieldRow = (field: string, desc: string, idx: number) => (
      <Box key={`${field}-${idx}`} sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
          {field}
        </Typography>
        <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
          {desc}
        </Typography>
      </Box>
    );

    return (
      <Box sx={{ flex: 1, p: 4, background: '#fff', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
              Rule Instructions & Features
            </Typography>
          </Box>

          {sectionTitle('Required Fields (Must Be Filled)')}
          {[
            { field: 'Rule Name', desc: 'A unique name for the rule. If similar rules are needed, use suffixes such as -1, -2, etc.' },
            { field: 'Redirect URL', desc: 'The destination URL where users will land after scanning. The URL must start with https://.' },
          ].map((item, i) => fieldRow(item.field, item.desc, i))}

          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
              Priority
            </Typography>
            <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
              Choose how important this rule is. If <strong>two or more rules match the same QR scan</strong>, the one with the <strong>highest priority wins</strong>.
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
              <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                <strong>Low</strong> — Baseline priority
              </Typography>
              <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                <strong>Medium</strong> — Standard priority
              </Typography>
              <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                <strong>High</strong> — Important priority
              </Typography>
              <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                <strong>Critical</strong> — Urgent override (overrides all other rules)
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px' }}>
              Example: If one rule is set to <strong>"Medium"</strong> and another to <strong>"Critical"</strong> for the same product, the <strong>"Critical" rule will be used</strong>.
            </Typography>
          </Box>

          {sectionTitle('User Roles & Access')}
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2, mb: 2 }}>
            <Typography variant="body1" sx={{ color: '#2E7D32', fontSize: '17px', fontWeight: 700, mb: 0.5 }}>
              Important
            </Typography>
            <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px', fontWeight: 400 }}>
              The dashboard has two AD groups — Admin and User. Your access level depends on which group you belong to.
            </Typography>
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eee', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
              <Box sx={{ minWidth: 6, height: 6, borderRadius: '50%', bgcolor: '#1a1a1a', mt: 1 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                  Admin
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                  Admins have <strong>full access</strong> to the entire dashboard. They can <strong>approve, reject, and delete</strong> rules. They also have access to the <strong>Audit Log</strong>, where they can track everything about a rule — who submitted it, who approved it, who made changes, who rejected it, and who deleted it.
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.5, bgcolor: '#e8f5e9', px: 1.5, py: 0.8, borderRadius: 1, display: 'inline-block' }}>
                  For example, the CPP (Connected Products Platform) team members are Admins — they manage and oversee all the rules in the dashboard.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ minWidth: 6, height: 6, borderRadius: '50%', bgcolor: '#1a1a1a', mt: 1 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                  User
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                  Users have access to the <strong>Rules</strong> page, <strong>Approvals</strong> page, and the <strong>Docs</strong> page. They can create and submit rules, but cannot approve, reject, or delete them.
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.5, bgcolor: '#e8f5e9', px: 1.5, py: 0.8, borderRadius: 1, display: 'inline-block' }}>
                  For example, Kevin from the Marketing team is a User — he can create and submit QR redirect rules for campaigns, but an Admin needs to approve them before they go live.
                </Typography>
              </Box>
            </Box>
          </Paper>

          {sectionTitle('Scheduling (Optional)')}
          <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mb: 2 }}>
            Set <strong>when the rule should start</strong> and <strong>when it should stop</strong>. If you leave these fields blank, the rule will become active right after approval and will run forever.
          </Typography>

          <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eee', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
              <Box sx={{ minWidth: 6, height: 6, borderRadius: '50%', bgcolor: '#1a1a1a', mt: 1 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                  Rule Start Date & Start Time
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                  Select the <strong>date and time</strong> when the rule should go live. Once the start date and time arrive, the rule will <strong>automatically become active</strong> after approval.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
              <Box sx={{ minWidth: 6, height: 6, borderRadius: '50%', bgcolor: '#1a1a1a', mt: 1 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                  Rule End Date & End Time
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                  Select the <strong>date and time</strong> when the rule should stop. Once the end date and time pass, the rule will <strong>automatically become inactive</strong> and QR scans will no longer be redirected by this rule.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ minWidth: 6, height: 6, borderRadius: '50%', bgcolor: '#1a1a1a', mt: 1 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                  Rule Has No Expiration
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                  Select this option if the rule should <strong>run indefinitely with no end date</strong>. The rule will only have a start date and will <strong>stay active forever</strong> after approval.
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ color: '#2E7D32', fontSize: '17px', fontWeight: 700, mb: 0.5 }}>
              Note
            </Typography>
            <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px', fontWeight: 400 }}>
              The end date must always be after the start date.
            </Typography>
          </Paper>

          {sectionTitle('Rule Status')}
          <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mb: 2 }}>
            After you create a rule, it moves through different statuses based on admin actions and scheduling. Here are the four statuses:
          </Typography>

          <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eee', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
              <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: '#FF9800', mt: 0.9 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                  Submitted
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                  You have <strong>created and submitted</strong> the rule. It is <strong>waiting for admin approval</strong>.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
              <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: '#2196F3', mt: 0.9 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                  Scheduled
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                  The admin has <strong>approved</strong> the rule, but the <strong>start date hasn't arrived yet</strong>. The rule will automatically go live on the start date.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
              <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: '#4CAF50', mt: 0.9 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                  Active
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                  The rule is <strong>live and running</strong>. The admin approved it and the start date has arrived. QR scans are now being redirected by this rule.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ minWidth: 8, height: 8, borderRadius: '50%', bgcolor: '#F44336', mt: 0.9 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                  Inactive
                </Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                  The rule is <strong>no longer active</strong>. This happens when the <strong>end date has passed</strong> (rule expired) or the <strong>admin has rejected</strong> the rule.
                </Typography>
              </Box>
            </Box>
          </Paper>

          {sectionTitle('Matching Criteria (Optional)')}
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2, mb: 2 }}>
            <Typography variant="body1" sx={{ color: '#2E7D32', fontSize: '17px', fontWeight: 700, mb: 0.5 }}>
              Important
            </Typography>
            <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px', fontWeight: 400 }}>
              Every rule requires at least one matching criterion — such as Style Codes, Style Color Codes, Country, Brand, Category, Division, GTINs, QR Codes, or Bounding Box. Without one, the rule cannot be submitted.
            </Typography>
          </Paper>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '20px' }}>
              Bounding Box
            </Typography>
            <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
              Bounding boxes allow geographic targeting using latitude and longitude coordinates.
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
              <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                North Latitude: -90 to 90
              </Typography>
              <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                South Latitude: -90 to 90
              </Typography>
              <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                East Longitude: -180 to 180
              </Typography>
              <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                West Longitude: -180 to 180
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mb: 1.5 }}>
              Validation rules: North must be greater than South, and East must be greater than West.
            </Typography>

            <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f5f5f5', borderRadius: 2, mt: 1.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '17px' }}>
                Bounding Box
              </Typography>
              <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                To simplify bounding box creation, use the free online tool at{' '}
                <strong>
                  <a href="https://boundingbox.klokantech.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0', textDecoration: 'underline' }}>
                    https://boundingbox.klokantech.com/
                  </a>
                </strong>
              </Typography>
              <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '17px', mt: 1, fontWeight: 600 }}>
                Steps:
              </Typography>
              <Box component="ol" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                  Open the link in a browser.
                </Typography>
                <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                  Search for a location or zoom to the desired area.
                </Typography>
                <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                  Click the bounding box option and draw a rectangle on the map.
                </Typography>
                <Typography component="li" variant="body2" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                  Copy the generated coordinates (for example: minLon, minLat, maxLon, maxLat).
                </Typography>
              </Box>
            </Paper>
          </Box>

      </Box>
    );
  }

  if (docsView === 'bulk-upload') {
    return (
      <Box sx={{ flex: 1, p: 4, background: '#fff', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
              Bulk Rule Creation
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: '#1a1a1a', mb: 3, fontSize: '17px' }}>
            Instead of creating rules one by one, you can import multiple QR redirect rules at once using an Excel file. This is ideal when you need to set up many rules quickly — for example, during a campaign launch or seasonal rollout.
          </Typography>

          <Paper elevation={0} sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: '20px', color: '#1a1a1a' }}>
              How It Works
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center', p: 1.5, flex: '1 1 120px', minWidth: 120 }}>
                <DownloadIcon sx={{ fontSize: 36, color: '#AB47BC' }} />
                <Typography variant="body1" sx={{ fontWeight: 700, mt: 1, fontSize: '16px' }}>Download Template</Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '15px' }}>Get the Excel template</Typography>
              </Box>
              <ArrowRightAltIcon sx={{ color: '#666', fontSize: 48, flexShrink: 0 }} />
              <Box sx={{ textAlign: 'center', p: 1.5, flex: '1 1 120px', minWidth: 120 }}>
                <InsertDriveFileIcon sx={{ fontSize: 36, color: '#4CAF50' }} />
                <Typography variant="body1" sx={{ fontWeight: 700, mt: 1, fontSize: '16px' }}>Fill in Rules</Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '15px' }}>One rule per row</Typography>
              </Box>
              <ArrowRightAltIcon sx={{ color: '#666', fontSize: 48, flexShrink: 0 }} />
              <Box sx={{ textAlign: 'center', p: 1.5, flex: '1 1 120px', minWidth: 120 }}>
                <UploadFileIcon sx={{ fontSize: 36, color: '#2196F3' }} />
                <Typography variant="body1" sx={{ fontWeight: 700, mt: 1, fontSize: '16px' }}>Upload & Create</Typography>
                <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '15px' }}>Review, select, and submit</Typography>
              </Box>
            </Box>
          </Paper>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: '20px', color: '#1a1a1a' }}>
            Step-by-Step Guide
          </Typography>

          {[
            { step: 1, text: 'Go to the Rules page', detail: 'Log in to the dashboard and click "Rules" in the sidebar.' },
            { step: 2, text: 'Click the "Bulk Upload" button', detail: 'You\'ll find the Bulk Upload option on the Rules page toolbar.' },
            { step: 3, text: 'Download the Excel template', detail: 'Click "Download Template" to get the pre-formatted Excel file with all the required columns.' },
            { step: 4, text: 'Fill in your rules in the template', detail: 'Enter one rule per row. Fill in all required columns: Rule Name, Rule Type, Style Code, Country, Start Date, End Date, Redirect URL, Brand, GTIN, and Category.' },
            { step: 5, text: 'Upload the completed Excel file', detail: 'Click "Upload" and select your filled-in Excel file. The system will read and validate all rows.' },
            { step: 6, text: 'Review the validation results', detail: 'The system will show which rows are valid and which have errors. Fix any errors in the Excel file and re-upload if needed.' },
            { step: 7, text: 'Select the rows you want to create', detail: 'Use the checkboxes to select the valid rows you want to create as rules. You can select all or pick specific ones.' },
            { step: 8, text: 'Click "Create Rules" to submit', detail: 'All selected rules will be created and submitted for approval. An admin will review and approve them.' },
          ].map((item) => (
            <Box key={item.step} sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#000', minWidth: 18, mt: 0.1 }}>
                  {item.step}.
                </Typography>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a1a', fontSize: '17px' }}>
                    {item.text}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '16px', mt: 0.3 }}>
                    {item.detail}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}

          <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f8f0fc', borderRadius: 2, border: '1px solid #dbb8e8', mt: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#7E57C2', mb: 1, fontSize: '17px' }}>Tips for Bulk Upload</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 20, color: '#AB47BC', mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px' }}>Always use the <strong>latest template</strong> — download it fresh each time to ensure correct columns.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 20, color: '#AB47BC', mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px' }}>Keep <strong>one rule per row</strong> — do not merge cells or add extra sheets.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 20, color: '#AB47BC', mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px' }}>Check the <strong>validation results</strong> carefully before creating — rows with errors will be skipped.</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 20, color: '#AB47BC', mt: 0.3, flexShrink: 0 }} />
              <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px' }}>All bulk-created rules still require <strong>admin approval</strong> before going live.</Typography>
            </Box>
          </Paper>
      </Box>
    );
  }

  if (docsView === 'adobe-analytics') {
    return (
      <Box sx={{ flex: 1, p: 4, background: '#fff', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
              How to Request Access
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '20px', color: '#1a1a1a' }}>
            Request Access for Claravine <Typography component="span" sx={{ fontWeight: 400, fontSize: '16px', color: '#000' }}>(Only for Admins - Connected Products Platform)</Typography>
          </Typography>
          <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eee', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '17px', color: '#1a1a1a' }}>
              How to Request Access
            </Typography>
            <Box component="ol" sx={{ pl: 2, mt: 0, mb: 0 }}>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                Go to ID Locker
              </Typography>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                Search for the <strong>App.Claravine.ViewandSubmit</strong>
              </Typography>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                Submit the access request
              </Typography>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                Request for <strong>GLOBAL - ORGANIC SOCIAL EARNED MEDIA</strong> to the Claravine team — this is required for viewing the data set inside Claravine
              </Typography>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                Wait for approval — if approved, you will receive a confirmation mail
              </Typography>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#ffebee', borderRadius: 2, border: '1px solid #ffcdd2', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '17px', color: '#d32f2f' }}>
              Once You Have Access
            </Typography>
            <Box component="ol" sx={{ pl: 2, mt: 0, mb: 0 }}>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                Download the extension and add it to Chrome for Claravine generating CP code from the GitHub repo <strong>claravine-selenium-automation</strong>
              </Typography>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                After the approval process is done, a new Claravine tab can be opened — stay on that page and do not move until the CP code is completely generated
              </Typography>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                It will update the existing URL with the generated CP code — the updated tracking URL can be seen in the rule details view
              </Typography>
            </Box>
          </Paper>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, mt: 3, fontSize: '20px', color: '#1a1a1a' }}>
            What is Adobe Analytics?
          </Typography>
          <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '17px', mb: 3 }}>
            Adobe Analytics helps you track how your QR redirect rules are performing — including views, scans, redirects, and user behavior. It provides insights into which rules are most effective and how consumers interact with your QR codes.
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '20px', color: '#1a1a1a' }}>
            Request Access to Adobe Analytics
          </Typography>
          <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eee', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: '17px', color: '#1a1a1a' }}>
              How to Request Access
            </Typography>
            <Box component="ol" sx={{ pl: 2, mt: 0, mb: 0 }}>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                Go to Slack
              </Typography>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                In Slack, search for the <strong>#adobe-analytics-support</strong> channel
              </Typography>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                There will be an option like <strong>"Request Access"</strong> — click on that. The accessbot automatically submits an IDLocker request for the <strong>App.Adobe.Analytics.global.user</strong> AD group, and you will get a confirmation with a request ID to track approval status.
              </Typography>
              <Typography component="li" variant="body1" sx={{ fontSize: '16px', color: '#1a1a1a', mb: 0.5 }}>
                After approval, you will receive a mail confirming that you now have access to Adobe Analytics
              </Typography>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#ffebee', borderRadius: 2, mt: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#c62828', mb: 1, fontSize: '17px' }}>
              Once You Have Access
            </Typography>
            <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px' }}>
              Click the "Adobe Analytics" button in the dashboard header to open Adobe Analytics directly and start tracking your QR redirect rule performance.
            </Typography>
          </Paper>
      </Box>
    );
  }

  const S = 180;
  const GAP = 20;

  const boxData = [
    { ...cards[0], color: '#2196F3', shadow: '#64B5F6', accentSide: 'left',   row: 0, col: 0, sx: -22, sy: -22, hsx: -26, hsy: -26 },
    { ...cards[1], color: '#4CAF50', shadow: '#81C784', accentSide: 'top',    row: 0, col: 1, sx: 22,  sy: -22, hsx: 26,  hsy: -26 },
    { ...cards[3], color: '#9C27B0', shadow: '#BA68C8', accentSide: 'bottom', row: 1, col: 0, sx: -22, sy: 22,  hsx: -26, hsy: 26 },
    { ...cards[2], color: '#F44336', shadow: '#E57373', accentSide: 'right',  row: 1, col: 1, sx: 22,  sy: 22,  hsx: 26,  hsy: 26 },
  ];

  const cW = S * 2 + GAP;
  const cH = S * 2 + GAP;
  const halfC = cW / 2;

  const lineLen = 80;
  const dotSz = 10;
  const txtW = 220;
  const topRowY = 120;
  const botRowY = 450;

  const connector = (
    item: typeof boxData[0],
    y: number,
    side: 'left' | 'right',
    cls: string,
  ) => {
    const isL = side === 'left';
    const lineStart = `calc(50% + ${halfC + 4}px)`;
    const dotPos = `calc(50% + ${halfC + lineLen - 1}px)`;
    const txtPos = `calc(50% + ${halfC + lineLen + dotSz + 8}px)`;

    return (
      <React.Fragment key={`c-${item.id}`}>
        <Box sx={{
          position: 'absolute', top: y,
          ...(isL ? { right: lineStart } : { left: lineStart }),
          width: lineLen, borderTop: '2px dashed #d0d0d0', zIndex: 0,
        }} />
        <Box sx={{
          position: 'absolute', top: y - dotSz / 2,
          ...(isL ? { right: dotPos } : { left: dotPos }),
          width: dotSz, height: dotSz, borderRadius: '50%',
          border: '2px solid #bbb', bgcolor: '#fff', zIndex: 3,
        }} />
        <Box
          onClick={() => setDocsView(item.id)}
          sx={{
            position: 'absolute', top: y,
            transform: 'translateY(-50%)',
            ...(isL ? { right: txtPos } : { left: txtPos }),
            width: txtW,
            textAlign: 'left',
            cursor: 'pointer',
            [`&:hover .${cls}`]: { color: item.color },
          }}
        >
          <Box>
            <Typography className={cls} sx={{
              fontWeight: 700, fontSize: '18px', color: '#000', mb: 0.5,
              transition: 'color 0.2s',
              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
              lineHeight: 1.3,
            }}>
              {item.title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '14px', lineHeight: 1.6 }}>
              {item.description}
            </Typography>
          </Box>
        </Box>
      </React.Fragment>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        pt: 3, px: 3, pb: 3,
        bgcolor: '#fff',
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100%',
        position: 'relative',
      }}
    >
      <IconButton onClick={() => onNavigate?.('my-rules')} sx={{ position: 'absolute', top: 16, left: 16, color: '#9e9e9e', transition: 'color 0.2s, background-color 0.2s', '&:hover': { color: '#1a1a1a', bgcolor: '#e0e0e0' }, '&:active': { color: '#1a1a1a', bgcolor: '#e0e0e0' } }}>
        <ArrowBackIcon />
      </IconButton>
      <Box sx={{ maxWidth: 880, width: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h4" sx={{
            fontWeight: 700, color: '#1a1a1a', mb: 0.5, fontSize: '34px',
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
          }}>
            Documentation
          </Typography>
          <Typography variant="body1" sx={{ color: '#1a1a1a', fontSize: '16px' }}>
            Learn how to use the QR Redirect Automation dashboard
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', width: '100%', height: 570, mx: 'auto' }}>
          {/* 2×2 card cluster */}
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            width: cW, height: cH,
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
          }}>
            {boxData.map((item) => (
              <Paper
                key={item.id}
                onClick={() => setDocsView(item.id)}
                elevation={0}
                sx={{
                  position: 'absolute',
                  top: item.row * (S + GAP),
                  left: item.col * (S + GAP),
                  width: S, height: S,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '14px',
                  bgcolor: '#fff',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  boxShadow: `${item.sx}px ${item.sy}px 0px ${item.shadow}, 0px 2px 8px rgba(0,0,0,0.06)`,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  zIndex: 2,
                  '&:hover': {
                    transform: 'scale(1.06)',
                    boxShadow: `${item.hsx}px ${item.hsy}px 0px ${item.shadow}, 0px 4px 14px rgba(0,0,0,0.1)`,
                    zIndex: 10,
                  },
                }}
              >
                {React.cloneElement(item.icon as React.ReactElement<any>, {
                  sx: { fontSize: 58, color: item.color },
                })}
              </Paper>
            ))}
          </Box>

          {connector(boxData[0], topRowY, 'left',  'dt01')}
          {connector(boxData[1], topRowY, 'right', 'dt02')}
          {connector(boxData[2], botRowY, 'left',  'dt04')}
          {connector(boxData[3], botRowY, 'right', 'dt03')}
        </Box>
      </Box>
    </Box>
  );
};

export default DocsPage;
